import React, { createContext, useContext, useState, useEffect } from 'react';
import { upgradeToPremium as serviceUpgradeToPremium } from '../services/subscriptionService';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SUBSCRIPTION_TIERS = {
  FREE: 'free',
  PREMIUM: 'premium',
  ULTRA: 'ultra',
};

// Tier hierarchy for comparison (higher number = higher tier)
export const TIER_HIERARCHY = {
  [SUBSCRIPTION_TIERS.FREE]: 0,
  [SUBSCRIPTION_TIERS.PREMIUM]: 1,
  [SUBSCRIPTION_TIERS.ULTRA]: 2,
};

export const FEATURES = {
  // Basic Premium Features (Premium+)
  WEEKLY_MATCHES_SLOT_2: 'weekly_matches_slot_2',
  WEEKLY_MATCHES_SLOT_3: 'weekly_matches_slot_3',
  UNLIMITED_MESSAGES: 'unlimited_messages',
  SEE_WHO_VIEWED: 'see_who_viewed',
  ADVANCED_AI: 'advanced_ai',
  PRIORITY_VISIBILITY: 'priority_visibility',
  PREMIUM_BADGE: 'premium_badge',
  INSTANT_NOTIFICATIONS: 'instant_notifications',
  ENHANCED_PRIVACY: 'enhanced_privacy',

  // Ultra-Exclusive Features (Ultra only)
  CONCIERGE_SERVICE: 'concierge_service',
  VERIFIED_BADGE: 'verified_badge',
  BACKGROUND_CHECK: 'background_check',
  VIDEO_DATES_PRIORITY: 'video_dates_priority',
  EXCLUSIVE_EVENTS: 'exclusive_events',
  PROFILE_BOOST: 'profile_boost',
  ADVANCED_FILTERS: 'advanced_filters',
  UNLIMITED_REWINDS: 'unlimited_rewinds',
};

const FEATURE_ACCESS_MAP = {
  [SUBSCRIPTION_TIERS.FREE]: [],
  [SUBSCRIPTION_TIERS.PREMIUM]: [
    FEATURES.WEEKLY_MATCHES_SLOT_2,
    FEATURES.WEEKLY_MATCHES_SLOT_3,
    FEATURES.UNLIMITED_MESSAGES,
    FEATURES.SEE_WHO_VIEWED,
    FEATURES.ADVANCED_AI,
    FEATURES.PRIORITY_VISIBILITY,
    FEATURES.PREMIUM_BADGE,
    FEATURES.INSTANT_NOTIFICATIONS,
    FEATURES.ENHANCED_PRIVACY,
  ],
  [SUBSCRIPTION_TIERS.ULTRA]: [
    // Ultra gets all Premium features plus Ultra-exclusive features
    FEATURES.WEEKLY_MATCHES_SLOT_2,
    FEATURES.WEEKLY_MATCHES_SLOT_3,
    FEATURES.UNLIMITED_MESSAGES,
    FEATURES.SEE_WHO_VIEWED,
    FEATURES.ADVANCED_AI,
    FEATURES.PRIORITY_VISIBILITY,
    FEATURES.PREMIUM_BADGE,
    FEATURES.INSTANT_NOTIFICATIONS,
    FEATURES.ENHANCED_PRIVACY,
    FEATURES.CONCIERGE_SERVICE,
    FEATURES.VERIFIED_BADGE,
    FEATURES.BACKGROUND_CHECK,
    FEATURES.VIDEO_DATES_PRIORITY,
    FEATURES.EXCLUSIVE_EVENTS,
    FEATURES.PROFILE_BOOST,
    FEATURES.ADVANCED_FILTERS,
    FEATURES.UNLIMITED_REWINDS,
  ],
};

export const SubscriptionProvider = ({ children }) => {
  const [subscription, setSubscription] = useState({
    tier: SUBSCRIPTION_TIERS.FREE,
    expiresAt: null,
    planId: null,
    isActive: false,
  });

  const [loading, setLoading] = useState(true);

  // Load subscription from localStorage or API
  useEffect(() => {
    const loadSubscription = async () => {
      try {
        // First check localStorage
        const stored = localStorage.getItem('subscription');
        if (stored) {
          const parsed = JSON.parse(stored);
          // Check if subscription is still valid
          if (parsed.expiresAt && new Date(parsed.expiresAt) > new Date()) {
            setSubscription({
              ...parsed,
              isActive: true,
            });
          } else if (parsed.expiresAt) {
            // Subscription expired
            setSubscription({
              tier: SUBSCRIPTION_TIERS.FREE,
              expiresAt: null,
              planId: null,
              isActive: false,
            });
            localStorage.removeItem('subscription');
          }
        }

        // TODO: Fetch from API to verify/sync subscription status
        // const response = await fetch('/api/subscription/status');
        // const data = await response.json();
        // setSubscription(data);
      } catch (error) {
        console.error('Failed to load subscription:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSubscription();
  }, []);

  // Save subscription to localStorage when it changes
  useEffect(() => {
    if (!loading && subscription.tier !== SUBSCRIPTION_TIERS.FREE) {
      localStorage.setItem('subscription', JSON.stringify(subscription));
    }
  }, [subscription, loading]);

  const hasFeatureAccess = (feature) => {
    if (!subscription.isActive && subscription.tier === SUBSCRIPTION_TIERS.FREE) {
      return false;
    }
    return FEATURE_ACCESS_MAP[subscription.tier]?.includes(feature) || false;
  };

  const isPremium = () => {
    return subscription.tier === SUBSCRIPTION_TIERS.PREMIUM && subscription.isActive;
  };

  const isUltra = () => {
    return subscription.tier === SUBSCRIPTION_TIERS.ULTRA && subscription.isActive;
  };

  const isPremiumOrHigher = () => {
    const tierLevel = TIER_HIERARCHY[subscription.tier] || 0;
    return subscription.isActive && tierLevel >= TIER_HIERARCHY[SUBSCRIPTION_TIERS.PREMIUM];
  };

  const getTierLevel = () => {
    return TIER_HIERARCHY[subscription.tier] || 0;
  };

  const hasTierAccess = (requiredTier) => {
    if (!subscription.isActive) return false;
    const currentLevel = TIER_HIERARCHY[subscription.tier] || 0;
    const requiredLevel = TIER_HIERARCHY[requiredTier] || 0;
    return currentLevel >= requiredLevel;
  };

  const upgradeSubscription = async (plan) => {
    try {
      // Map UI plan to server tier for checkout
      // premium_* -> 'premium', ultra_* -> 'elite'
      let mappedTier = 'premium';
      if (plan?.tier === SUBSCRIPTION_TIERS.ULTRA || String(plan?.id || '').startsWith('ultra_')) {
        mappedTier = 'elite';
      }

      // Initiate Stripe Checkout via backend; this will redirect the browser
      const result = await serviceUpgradeToPremium(mappedTier);
      return result;
    } catch (error) {
      console.error('Upgrade failed:', error);
      return { success: false, error: error.message };
    }
  };

  const cancelSubscription = async () => {
    try {
      // TODO: Call API to cancel subscription
      // const response = await fetch('/api/subscription/cancel', {
      //   method: 'POST',
      // });

      setSubscription({
        tier: SUBSCRIPTION_TIERS.FREE,
        expiresAt: null,
        planId: null,
        isActive: false,
      });

      localStorage.removeItem('subscription');
      return { success: true };
    } catch (error) {
      console.error('Cancellation failed:', error);
      return { success: false, error: error.message };
    }
  };

  const getRemainingDays = () => {
    if (!subscription.expiresAt) return 0;
    const now = new Date();
    const expires = new Date(subscription.expiresAt);
    const diffTime = expires - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const value = {
    subscription,
    loading,
    isPremium,
    isUltra,
    isPremiumOrHigher,
    getTierLevel,
    hasTierAccess,
    hasFeatureAccess,
    upgradeSubscription,
    upgradeToPremium: upgradeSubscription, // Alias for backwards compatibility
    cancelSubscription,
    getRemainingDays,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
